/**
 * v0.9.1416: the invoice a customer actually opens.
 *
 * Reached at /invoice/<token>, with no login - the person paying is an
 * accounts payable clerk who will never have one. The token is the
 * credential, which is why nothing here asks who they are and nothing is
 * shown that the bearer should not already know.
 *
 * THREE THINGS IT HAS TO DO, in the order a customer does them:
 *
 *   1. Say what is owed, plainly enough to approve without a phone call.
 *   2. Show WHAT WAS EATEN, itemised, because "what was this $86.21 on the
 *      14th" is the question that stalls payment.
 *   3. Take the money.
 *
 * Printing is the browser's own, not a generated PDF. A finance department
 * files a printout or a browser PDF just as happily, and a server-rendered
 * PDF is a second layout to keep correct forever.
 */

import { useEffect, useRef, useState } from 'react';
import { ensureCollectJsLoaded, configureInlineFields } from '../lib/nmi';
/*
 * v0.9.1418: the backend lives on Cloud Run, not on this domain.
 *
 * A bare relative fetch went to Netlify, which answers every unknown path
 * with index.html - so the page received HTML, tried to JSON.parse a '<'
 * and died with Safari's "The string did not match the expected pattern".
 * Every other caller in the app already goes through BACKEND_URL.
 */
import { BACKEND_URL } from '../lib/api';
import { usablePhotoUrl } from '../lib/photoUrl';

type Charge = {
  entryId: string;
  amountCents: number;
  tipCents: number;
  createdAt: string;
  note: string | null;
  orderId: string | null;
  ticketNumber: number | null;
  salesDate: string | null;
};

type InvoiceData = {
  invoice: {
    number: number;
    periodStart: string | null;
    periodEnd: string | null;
    subtotalCents: number;
    tipCents: number;
    taxCents: number;
    totalCents: number;
    paidCents: number;
    balanceCents: number;
    status: string;
    dueAt: string | null;
  };
  account: { name: string; contactName: string | null };
  business: { name: string; phone: string | null; logoUrl: string | null };
  charges: Charge[];
  payment: { tokenizationKey: string; sandbox: boolean } | null;
};

type OrderDetail = {
  id: string;
  ticketNumber: number | null;
  salesDate: string | null;
  guestCount: number | null;
  subtotalCents: number;
  taxCents: number;
  tipCents: number;
  /** v0.9.1424: an auto party charge, not a voluntary tip. */
  gratuityCents: number;
  discountCents: number;
  /** dine-in | takeout | delivery | pickup */
  type: string | null;
  serverName: string | null;
  guestName: string | null;
  totalCents: number;
  items: Array<{
    name: string;
    qty: number;
    lineTotalCents: number;
    modifiers: Array<{ name?: string; label?: string }> | null;
  }>;
};

const money = (c: number): string => '$' + (c / 100).toFixed(2);

export default function InvoicePage(): JSX.Element {
  const token = window.location.pathname.split('/').filter(Boolean)[1] ?? '';
  /*
   * v0.9.1419: /statement/<token> shows every unpaid invoice at once and
   * pays any combination in ONE card charge. A customer with three open
   * invoices was otherwise being asked to enter a card three times, which
   * nobody does - they ring up and ask for a total instead.
   */
  const isStatement = window.location.pathname.startsWith('/statement/');
  if (isStatement) return <StatementPage token={token} />;
  const [data, setData] = useState<InvoiceData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderDetail[] | null>(null);
  /**
   * v0.9.1418: which individual charges are expanded.
   *
   * Every charge is its own openable line rather than one "see everything"
   * blob, because a customer querying an invoice is querying ONE line -
   * "what was the 14th" - and making them open a wall of six visits to
   * find it is the same as not answering.
   */
  const [openOrders, setOpenOrders] = useState<Set<string>>(() => new Set());
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [amountText, setAmountText] = useState('');
  const [busy, setBusy] = useState(false);
  const [paidNote, setPaidNote] = useState<string | null>(null);
  const tokenizeRef = useRef<(() => Promise<{ token: string }>) | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(BACKEND_URL + '/v1/public/invoices/' + encodeURIComponent(token));
        if (!res.ok) throw new Error(res.status === 404 ? 'Invoice not found.' : 'Could not load this invoice.');
        const d = await res.json() as InvoiceData;
        if (cancelled) return;
        setData(d);
        // Prefilled with the full balance - the common case is paying it
        // all, and a customer who wants to part-pay can edit one field.
        setAmountText((d.invoice.balanceCents / 100).toFixed(2));

        if (d.payment && d.invoice.balanceCents > 0) {
          await ensureCollectJsLoaded(d.payment.tokenizationKey, d.payment.sandbox);
          if (cancelled) return;
          const handle = configureInlineFields({
            ccnumberSelector: '#inv-ccnumber',
            ccexpSelector: '#inv-ccexp',
            cvvSelector: '#inv-cvv',
          });
          tokenizeRef.current = handle.tokenize as unknown as () => Promise<{ token: string }>;
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message || 'Could not load this invoice.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  /** Fetched once, then every line can open instantly. */
  async function ensureOrders(): Promise<OrderDetail[]> {
    if (orders) return orders;
    setLoadingOrders(true);
    try {
      const res = await fetch(BACKEND_URL + '/v1/public/invoices/' + encodeURIComponent(token) + '/orders');
      const d = await res.json() as { orders: OrderDetail[] };
      setOrders(d.orders ?? []);
      return d.orders ?? [];
    } catch {
      setErr('Could not load the order detail.');
      return [];
    } finally {
      setLoadingOrders(false);
    }
  }

  async function toggleCharge(orderId: string | null): Promise<void> {
    if (!orderId) return;
    await ensureOrders();
    setOpenOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  }

  /** Open every line at once - what someone printing the invoice wants. */
  async function expandAll(): Promise<void> {
    const list = await ensureOrders();
    setOpenOrders(new Set(list.map((o) => o.id)));
  }

  async function pay(): Promise<void> {
    if (busy || !tokenizeRef.current || !data) return;
    const cents = Math.round(parseFloat(amountText) * 100);
    if (!Number.isFinite(cents) || cents <= 0) { setErr('Enter an amount to pay.'); return; }
    // Confirmed out loud. This is the last moment before a card is
    // charged, and the amount is the one thing that must not surprise.
    if (!window.confirm('Charge ' + money(Math.min(cents, data.invoice.balanceCents)) + ' to this card?')) return;
    setBusy(true); setErr(null);
    try {
      const t = await tokenizeRef.current();
      const res = await fetch(BACKEND_URL + '/v1/public/invoices/' + encodeURIComponent(token) + '/pay', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ paymentToken: t.token, amountCents: cents }),
      });
      const body = await res.json() as { paidCents?: number; remainingCents?: number; message?: string };
      if (!res.ok) throw new Error(body.message || 'The payment was declined.');
      setPaidNote(
        'Thank you. ' + money(body.paidCents ?? cents) + ' paid.'
        + ((body.remainingCents ?? 0) > 0
            ? ' ' + money(body.remainingCents as number) + ' still outstanding.'
            : ' This invoice is settled.'),
      );
    } catch (e) {
      setErr((e as Error).message || 'The payment could not be processed.');
    } finally {
      setBusy(false);
    }
  }

  if (err && !data) return <Shell><p style={{ color: '#b91c1c' }}>{err}</p></Shell>;
  if (!data) return <Shell><p style={{ color: '#666' }}>Loading...</p></Shell>;

  const inv = data.invoice;
  /*
   * Parsed once and shared by the button label and the request, so what a
   * customer is told they are paying and what is actually sent cannot
   * drift apart.
   */
  const parsedAmountCents = (() => {
    const n = Math.round(parseFloat(amountText) * 100);
    return Number.isFinite(n) && n > 0 ? Math.min(n, inv.balanceCents) : 0;
  })();

  return (
    <Shell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          {usablePhotoUrl(data.business.logoUrl) && (
            <img src={usablePhotoUrl(data.business.logoUrl) as string} alt="" style={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain', display: 'block', marginBottom: 8 }} />
          )}
          <div style={{ fontSize: 20, fontWeight: 700 }}>{data.business.name}</div>
          {data.business.phone && <div style={{ color: '#666', fontSize: 13 }}>{data.business.phone}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, color: '#666' }}>Invoice</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>#{inv.number}</div>
          {inv.dueAt && (
            <div style={{ fontSize: 13, color: '#666' }}>
              Due {new Date(inv.dueAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 13, color: '#666' }}>Billed to</div>
        <div style={{ fontWeight: 600 }}>{data.account.name}</div>
        {data.account.contactName && <div style={{ color: '#666' }}>{data.account.contactName}</div>}
        {inv.periodStart && inv.periodEnd && (
          <div style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
            {inv.periodStart} to {inv.periodEnd}
          </div>
        )}
      </div>

      {/* The charges. One line per visit, which is how a customer remembers
          them - not one line per item. */}
      {/* One openable line per visit. The detail sits directly under the
          line it belongs to, so a question about the 14th is answered
          without scrolling past five other days. */}
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: 12, paddingBottom: 6 }}>
          <span>Date</span>
          <span>Amount</span>
        </div>
        {data.charges.map((c) => {
          const detail = (orders ?? []).find((o) => o.id === c.orderId) ?? null;
          const isOpen = c.orderId != null && openOrders.has(c.orderId);
          return (
            <div key={c.entryId} style={{ borderTop: '1px solid #f1f5f9' }}>
              <button
                type="button"
                onClick={() => void toggleCharge(c.orderId)}
                disabled={!c.orderId}
                style={{
                  width: '100%', textAlign: 'left', background: 'none', border: 0,
                  padding: '10px 0', cursor: c.orderId ? 'pointer' : 'default',
                  display: 'flex', justifyContent: 'space-between', gap: 12,
                  font: 'inherit', color: 'inherit',
                }}
              >
                <span>
                  {c.salesDate ?? new Date(c.createdAt).toLocaleDateString()}
                  {c.ticketNumber != null && (
                    <span style={{ color: '#666' }}>{'  check #' + c.ticketNumber}</span>
                  )}
                  {c.tipCents > 0 && (
                    <span style={{ color: '#666' }}>{'  incl. ' + money(c.tipCents) + ' tip'}</span>
                  )}
                  {c.orderId && (
                    <span className="no-print" style={{ color: '#2563eb', fontSize: 13 }}>
                      {'  '}{isOpen ? 'hide items' : 'see items'}
                    </span>
                  )}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {money(c.amountCents)}
                </span>
              </button>

              {isOpen && detail && (
                <div style={{ padding: '0 0 12px 16px', borderLeft: '2px solid #e5e7eb', marginLeft: 4 }}>
                  {detail.items.map((it, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
                      <span>
                        {it.qty} x {it.name}
                        {(it.modifiers ?? []).length > 0 && (
                          <span style={{ color: '#666' }}>
                            {'  '}{(it.modifiers ?? []).map((m) => m.label || m.name).filter(Boolean).join(', ')}
                          </span>
                        )}
                      </span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(it.lineTotalCents)}</span>
                    </div>
                  ))}
                  {/*
                    * v0.9.1424: the questions a company actually asks -
                    * who authorised it, was it eaten in or collected, and
                    * was that charge a tip or an automatic gratuity.
                    * Each on its own line rather than run together,
                    * because they get read one at a time.
                    */}
                  <div style={{ fontSize: 13, color: '#666', marginTop: 8, lineHeight: 1.6 }}>
                    {detail.type && <div>Order type: {orderTypeLabel(detail.type)}</div>}
                    {detail.guestName && <div>Placed by: {detail.guestName}</div>}
                    {detail.serverName && <div>Served by: {detail.serverName}</div>}
                    {detail.guestCount ? <div>Guests: {detail.guestCount}</div> : null}
                  </div>

                  <div style={{ fontSize: 13, marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>
                    <Row label="Subtotal" value={money(detail.subtotalCents)} />
                    {detail.discountCents > 0 && (
                      <Row label="Discount" value={'-' + money(detail.discountCents)} />
                    )}
                    <Row label="Tax" value={money(detail.taxCents)} />
                    {/* Kept apart on purpose. An automatic party gratuity
                        and a voluntary tip are different things to a
                        finance department, and lumping them together is
                        what starts the query. */}
                    {detail.gratuityCents > 0 && (
                      <Row label="Gratuity (automatic)" value={money(detail.gratuityCents)} />
                    )}
                    {detail.tipCents > 0 && (
                      <Row label="Tip" value={money(detail.tipCents)} />
                    )}
                    <Row label="Check total" value={money(detail.totalCents)} bold />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {loadingOrders && (
          <div style={{ color: '#666', fontSize: 13, padding: '8px 0' }}>Loading items...</div>
        )}
      </div>

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #111', fontSize: 15 }}>
        <Row label="Subtotal" value={money(inv.subtotalCents)} />
        {inv.tipCents > 0 && <Row label="Tips" value={money(inv.tipCents)} />}
        {inv.taxCents > 0 && <Row label="Tax" value={money(inv.taxCents)} />}
        <Row label="Total" value={money(inv.totalCents)} bold />
        {inv.paidCents > 0 && <Row label="Paid" value={'-' + money(inv.paidCents)} />}
        <Row label="Balance due" value={money(inv.balanceCents)} bold />
      </div>

      {/* Itemised detail. Collapsed by default: most invoices are approved
          without it, and it is long. */}
      <div className="no-print" style={{ marginTop: 20 }}>
        {/*
          * v0.9.1420: back to the statement, when they came from one.
          *
          * A customer opening an invoice to check it needs a way back to
          * the page where they were paying. Browser back works, but only
          * if they trust it - and someone mid-payment does not experiment.
          */}
        {document.referrer.includes('/statement/') && (
          <button type="button" onClick={() => window.history.back()} style={{ ...ghost, marginRight: 8 }}>
            Back to statement
          </button>
        )}
        {/* For printing: a paper invoice should carry everything, because
            the person filing it cannot tap anything. */}
        <button type="button" onClick={() => void expandAll()} style={ghost}>
          Show all items
        </button>
        <button type="button" onClick={() => window.print()} style={{ ...ghost, marginLeft: 8 }}>
          Print / save PDF
        </button>
      </div>


      {/* Payment. Hidden once settled - an invoice with nothing owing
          should not present a card form. */}
      {inv.balanceCents > 0 && !paidNote && (
        <div className="no-print" style={{ marginTop: 28, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Pay this invoice</div>
          {!data.payment ? (
            <p style={{ color: '#666', fontSize: 14 }}>
              Card payment is not available. Please contact {data.business.name}
              {data.business.phone ? ' on ' + data.business.phone : ''} to settle this invoice.
            </p>
          ) : (
            <>
              <label style={{ display: 'block', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: '#666' }}>Amount to pay</span>
                <input
                  value={amountText}
                  onChange={(e) => setAmountText(e.target.value)}
                  inputMode="decimal"
                  style={{ ...field, maxWidth: 160 }}
                />
              </label>
              <div style={{ display: 'grid', gap: 8 }}>
                <div id="inv-ccnumber" style={field} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div id="inv-ccexp" style={field} />
                  <div id="inv-cvv" style={field} />
                </div>
              </div>
              {err && <div style={{ color: '#b91c1c', fontSize: 14, marginTop: 8 }}>{err}</div>}
              {/*
                * v0.9.1419: the button states the amount it will charge,
                * derived from the same parse the request uses - not from
                * the raw text. If they diverge the button is the thing
                * that shows it, before the card is charged rather than
                * after.
                */}
              <button type="button" disabled={busy} onClick={() => void pay()} style={primary}>
                {busy ? 'Processing...' : 'Pay ' + money(parsedAmountCents)}
              </button>
              {parsedAmountCents > 0 && parsedAmountCents < inv.balanceCents && (
                <div style={{ fontSize: 13, color: '#666', marginTop: 8, textAlign: 'center' }}>
                  Part payment. {money(inv.balanceCents - parsedAmountCents)} will remain outstanding.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {paidNote && (
        <div style={{
          marginTop: 24, padding: 16, borderRadius: 10,
          background: '#ecfdf5', border: '1px solid #10b981', color: '#065f46',
        }}>{paidNote}</div>
      )}
    </Shell>
  );
}

/**
 * v0.9.1424: order type in words a customer uses.
 *
 * The stored values are the POS's own vocabulary; "dine-in" is close
 * enough but "togo" is not something anyone says to their accountant.
 */
function orderTypeLabel(t: string): string {
  switch (t) {
    case 'dine-in': return 'Dine in';
    case 'takeout': return 'Takeout';
    case 'togo': return 'Takeout';
    case 'delivery': return 'Delivery';
    case 'pickup': return 'Pickup';
    case 'walk-in': return 'Walk in';
    case 'catering': return 'Catering';
    // An unrecognised type is shown as-is rather than hidden: a customer
    // seeing an odd word can ask, where a missing line tells them nothing.
    default: return t;
  }
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontWeight: bold ? 700 : 400 }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{
      minHeight: '100vh', background: '#f6f7f9', padding: 20,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#111',
    }}>
      {/* Print rules live here rather than a stylesheet: this page is the
          only thing in the product a customer prints, and the controls
          must not appear on the paper. */}
      {/* Only the controls. Visibility is handled globally in index.css -
          two competing print stylesheets is how this breaks again. */}
      <style>{'@media print { .no-print { display: none !important; } }'}</style>
      {/*
        * v0.9.1423: the id the global print rule looks for.
        *
        * index.css hides every element when printing except the ones named
        * there - a POS receipt rule from v0.9.1021 that predates this page.
        * Without this id the invoice printed as a blank sheet.
        */}
      <div id="invoice-print" style={{
        maxWidth: 640, margin: '0 auto', background: '#fff',
        border: '1px solid #e3e6ea', borderRadius: 12, padding: 24,
      }}>{children}</div>
    </div>
  );
}

const field: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  border: '1px solid #d1d5db', borderRadius: 8, fontSize: 16, background: '#fff',
  /*
   * v0.9.1419: an explicit colour, and colorScheme light.
   *
   * Without them iOS in dark mode renders input text WHITE on this white
   * card - so a customer editing the amount could not see what they had
   * typed, and could not tell whether the edit had registered at all. On
   * a payment field that is not a cosmetic bug: it is someone paying an
   * amount they did not choose.
   */
  color: '#111',
  colorScheme: 'light',
  minHeight: 42,
};

const primary: React.CSSProperties = {
  marginTop: 12, width: '100%', padding: 14, borderRadius: 10, border: 0,
  background: '#111', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
};

const ghost: React.CSSProperties = {
  padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db',
  background: '#fff', fontSize: 14, cursor: 'pointer',
};

type StatementInvoice = {
  id: string;
  invoiceNumber: number;
  periodStart: string | null;
  periodEnd: string | null;
  totalCents: number;
  paidCents: number;
  balanceCents: number;
  dueAt: string | null;
  /** v0.9.1420: its own detail page - already returned by the statement. */
  publicToken: string | null;
};

type StatementData = {
  account: { name: string; contactName: string | null };
  business: { name: string; phone: string | null; logoUrl: string | null };
  invoices: StatementInvoice[];
  /** Payable here and now. */
  totalDueCents: number;
  /** Eaten but not yet invoiced - owed, but not payable on this page. */
  uninvoicedCents: number;
  uninvoicedCount: number;
  /** The figure the restaurant quotes. */
  accountBalanceCents: number;
  payment: { tokenizationKey: string; sandbox: boolean } | null;
};

/**
 * v0.9.1419: every unpaid invoice on one page, paid in one charge.
 *
 * Invoices are all TICKED BY DEFAULT. The overwhelmingly common case is
 * "pay everything", and making someone tick three boxes to do the normal
 * thing is friction on the step where money arrives.
 */
function StatementPage({ token }: { token: string }): JSX.Element {
  const [data, setData] = useState<StatementData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [paidNote, setPaidNote] = useState<string | null>(null);
  const tokenizeRef = useRef<(() => Promise<{ token: string }>) | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(BACKEND_URL + '/v1/public/accounts/' + encodeURIComponent(token));
        if (!res.ok) throw new Error(res.status === 404 ? 'Statement not found.' : 'Could not load this statement.');
        const d = await res.json() as StatementData;
        if (cancelled) return;
        setData(d);
        setPicked(new Set(d.invoices.map((i) => i.id)));
        if (d.payment && d.invoices.length > 0) {
          await ensureCollectJsLoaded(d.payment.tokenizationKey, d.payment.sandbox);
          if (cancelled) return;
          const handle = configureInlineFields({
            ccnumberSelector: '#stm-ccnumber',
            ccexpSelector: '#stm-ccexp',
            cvvSelector: '#stm-cvv',
          });
          tokenizeRef.current = handle.tokenize as unknown as () => Promise<{ token: string }>;
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message || 'Could not load this statement.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (err && !data) return <Shell><p style={{ color: '#b91c1c' }}>{err}</p></Shell>;
  if (!data) return <Shell><p style={{ color: '#666' }}>Loading...</p></Shell>;

  const selected = data.invoices.filter((i) => picked.has(i.id));
  const selectedTotal = selected.reduce((s, i) => s + i.balanceCents, 0);

  async function pay(): Promise<void> {
    if (busy || !tokenizeRef.current || selected.length === 0) return;
    if (!window.confirm('Charge ' + money(selectedTotal) + ' to this card?')) return;
    setBusy(true); setErr(null);
    try {
      const t = await tokenizeRef.current();
      const res = await fetch(BACKEND_URL + '/v1/public/accounts/' + encodeURIComponent(token) + '/pay', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ paymentToken: t.token, invoiceIds: selected.map((i) => i.id) }),
      });
      const body = await res.json() as {
        paidCents?: number;
        applied?: Array<{ invoiceNumber: number; appliedCents: number }>;
        message?: string;
      };
      if (!res.ok) throw new Error(body.message || 'The payment was declined.');
      setPaidNote(
        'Thank you. ' + money(body.paidCents ?? selectedTotal) + ' paid across '
        + (body.applied?.length ?? selected.length) + ' invoice'
        + ((body.applied?.length ?? selected.length) === 1 ? '' : 's') + '.',
      );
    } catch (e) {
      setErr((e as Error).message || 'The payment could not be processed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          {usablePhotoUrl(data.business.logoUrl) && (
            <img src={usablePhotoUrl(data.business.logoUrl) as string} alt="" style={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain', display: 'block', marginBottom: 8 }} />
          )}
          <div style={{ fontSize: 20, fontWeight: 700 }}>{data.business.name}</div>
          {data.business.phone && <div style={{ color: '#666', fontSize: 13 }}>{data.business.phone}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, color: '#666' }}>Statement</div>
          {/*
            * v0.9.1427: the headline is the ACCOUNT BALANCE, not just what
            * is invoiced.
            *
            * It previously showed only the invoiced total, so an account
            * owing $50 with $6.71 not yet invoiced displayed $43.29 - and
            * a customer who paid that would be told days later they still
            * owed more. The page now shows what is owed and, separately,
            * what can be settled here.
            */}
          {/*
            * v0.9.1446: a negative balance is a CREDIT, not a debt.
            *
            * It rendered as "$-6.71" under "account balance", which reads
            * as an error rather than as money the customer is owed. Shown
            * as a positive number with the meaning stated instead.
            */}
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {money(Math.abs(data.accountBalanceCents))}
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>
            {data.accountBalanceCents > 0
              ? 'balance due'
              : data.accountBalanceCents < 0
                ? 'credit on account'
                : 'nothing due'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 13, color: '#666' }}>Account</div>
        <div style={{ fontWeight: 600 }}>{data.account.name}</div>
      </div>

      {data.invoices.length === 0 && data.uninvoicedCents === 0 && (
        <p style={{ color: '#666', marginTop: 20 }}>
          Nothing outstanding. Thank you.
        </p>
      )}

      {data.invoices.map((inv) => (
        /*
         * v0.9.1420: the checkbox and the detail link are SIBLINGS, not
         * nested.
         *
         * A link inside a <label> still toggles the checkbox on the way
         * out - so tapping "view" would silently untick the invoice being
         * inspected, and a customer could pay less than they meant to
         * without ever seeing why.
         */
        <div key={inv.id} style={{
          display: 'flex', gap: 12, alignItems: 'center',
          padding: '12px 0', borderTop: '1px solid #f1f5f9',
        }}>
          <label style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={picked.has(inv.id)}
              onChange={() => setPicked((prev) => {
                const next = new Set(prev);
                if (next.has(inv.id)) next.delete(inv.id); else next.add(inv.id);
                return next;
              })}
              style={{ width: 20, height: 20, flexShrink: 0 }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>Invoice #{inv.invoiceNumber}</span>
              <div style={{ fontSize: 13, color: '#666' }}>
                {inv.periodStart && inv.periodEnd ? inv.periodStart + ' to ' + inv.periodEnd : ''}
                {inv.dueAt ? '  due ' + new Date(inv.dueAt).toLocaleDateString() : ''}
                {inv.paidCents > 0 ? '  ' + money(inv.paidCents) + ' already paid' : ''}
              </div>
            </span>
          </label>
          {/* OUTSIDE the label, so tapping it opens the invoice instead of
              also unticking it on the way out. */}
          {inv.publicToken && (
            <a
              href={'/invoice/' + inv.publicToken}
              style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              See items
            </a>
          )}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, flexShrink: 0 }}>
            {money(inv.balanceCents)}
          </span>
        </div>
      ))}

      {/* Stated plainly rather than left as a gap between two numbers. A
          customer who reconciles and finds the page short by $6.71 rings
          up; one who is told why does not. */}
      {data.uninvoicedCents > 0 && (
        <div style={{
          marginTop: 16, padding: 12, borderRadius: 8,
          background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f', fontSize: 14,
        }}>
          {money(data.uninvoicedCents)} of recent charges
          {data.uninvoicedCount > 0 ? ' (' + data.uninvoicedCount + ' visit' + (data.uninvoicedCount === 1 ? '' : 's') + ')' : ''}
          {' '}has not been invoiced yet, so it cannot be paid here. It will
          appear on your next invoice.
        </div>
      )}

      {data.invoices.length > 0 && !paidNote && (
        <div style={{ marginTop: 24, paddingTop: 18, borderTop: '2px solid #111' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 17 }}>
            <span>Paying</span>
            <span>{money(selectedTotal)}</span>
          </div>

          {!data.payment ? (
            <p style={{ color: '#666', fontSize: 14, marginTop: 12 }}>
              Card payment is not available. Please contact {data.business.name}
              {data.business.phone ? ' on ' + data.business.phone : ''} to settle these invoices.
            </p>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                <div id="stm-ccnumber" style={field} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div id="stm-ccexp" style={field} />
                  <div id="stm-cvv" style={field} />
                </div>
              </div>
              {err && <div style={{ color: '#b91c1c', fontSize: 14, marginTop: 8 }}>{err}</div>}
              <button
                type="button"
                disabled={busy || selectedTotal <= 0}
                onClick={() => void pay()}
                style={{ ...primary, opacity: (busy || selectedTotal <= 0) ? 0.5 : 1 }}
              >
                {busy ? 'Processing...' : 'Pay ' + money(selectedTotal)}
              </button>
            </>
          )}
        </div>
      )}

      {paidNote && (
        <div style={{
          marginTop: 24, padding: 16, borderRadius: 10,
          background: '#ecfdf5', border: '1px solid #10b981', color: '#065f46',
        }}>{paidNote}</div>
      )}
    </Shell>
  );
}
